import { setLocale } from 'yup'
import { ru } from 'yup-locales'
import { PageLink, PageTitle } from '../../../../../../../_metronic/layout/core'
import { KTIcon } from '../../../../../../../_metronic/helpers'
import { useCallback, useEffect, useMemo, useState } from 'react'
import ReactPaginate from 'react-paginate'
import { serializeFiltersToUrl } from '../../../../../../../utils/statusFiltersURL'
import { Table } from '../../../../../../../components/Table'
import { TableHead } from '../../../../../../../components/Table/TableHead'
import { TableBody } from '../../../../../../../components/Table/TableBody'
import { LoaderForTable } from '../../../../../../../components/Loader'
import { useLocation } from 'react-router-dom'
import { REQUISITES_BREADCRUMBS } from '../../core/consts'
import RequisiteTypesItem from './RequisiteTypesItem'
import { IRequisiteType } from '../../core/response.types'
import { RequisiteTypeListQuery } from '../../core/query.types'
import { DEFAULT_REQUISITE_TYPE_QUERY } from '../../core/query.defaults'
import { getRequisiteTypeList } from '../../core/api'
import { ApplyFiltersArgs } from '../../core/types'
import ModalRequisiteType from '../common/ModalRequisiteType'
import { getPaymentMethods } from '../../../../api/getPaymentMethods'

setLocale(ru)

export default () => {
    const location = useLocation()
    const state = location.state as {
        initialRequisiteType?: string;
    } | null;

    const initialRequisiteType = state?.initialRequisiteType ?? '';

    const breadCrumbs = useMemo<PageLink[]>(
        () => REQUISITES_BREADCRUMBS.map((b) => ({
            ...b,
            isActive: location.pathname === b.path,
        })),
        [location.pathname]
    )

    const [initialized, setInitialized] = useState(false)
    const [isInitializing, setInitializing] = useState(false)
    const [isLoading, setLoading] = useState(false)

    const [requisiteTypes, setRequisiteTypes] = useState<IRequisiteType[]>([])
    const [requisiteTypeCount, setRequisiteTypeCount] = useState(0)

    const [pageCount, setPageCount] = useState(0)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [query, setQuery] = useState<RequisiteTypeListQuery>(DEFAULT_REQUISITE_TYPE_QUERY)

    const [editingRequisiteType, setEditingRequisiteType] = useState<IRequisiteType | null>(null)

    // ===== Справиочники =====
    const [lookupPaymentMethods, setLookupPaymentMethods] = useState<any[]>([])

    const initializePage = async () => {
            setInitializing(true)
    
            try {
                const response = await getPaymentMethods()
                if (!response?.data?.success) {
                    throw new Error(
                        'errors' in response?.data ? response?.data?.errors?.general_error : 'Unknown error'
                    )
                }
    
                const list = response.data.paymentMethods ?? []
    
                setLookupPaymentMethods(list)
            } catch (error) {
                console.error('Ошибка загрузки справочника:', error)
                setLookupPaymentMethods([])
            } finally {
                setInitializing(false)
            }
        }

    const loadRequisiteTypeList = useCallback(
        async (params: RequisiteTypeListQuery) => {
            setLoading(true)
            
            try {
                const response = await getRequisiteTypeList(params)
                if (!response?.data?.success) {
                    throw new Error(
                        'errors' in response?.data ? response?.data?.errors?.general_error : 'Unknown error'
                    )
                }

                const list = response.data.data || []
                const totalRows = response.data.totalRows || 0
                const pages = Math.ceil(totalRows / params.perPage)

                setRequisiteTypes(list)
                setRequisiteTypeCount(totalRows)
                setPageCount(pages)
            } catch (error) {
                console.error('Ошибка загрузки шаблонов:', error)
                setRequisiteTypes([])
                setRequisiteTypeCount(0)
                setPageCount(0)
            } finally {
                setLoading(false)
            }
        },
        []
    )

    const applyFilters = useCallback(
        ({ patch, page }: ApplyFiltersArgs = {}) => {
            
            const nextQuery: RequisiteTypeListQuery = {
                ...DEFAULT_REQUISITE_TYPE_QUERY,
                ...query,
                ...patch,
                page: page ?? patch?.page ?? query.page,
            }

            setQuery(nextQuery)
    
            const urlQuery = serializeFiltersToUrl(nextQuery, DEFAULT_REQUISITE_TYPE_QUERY)
            const nextUrl = urlQuery ? `${window.location.pathname}?${urlQuery}` : window.location.pathname
            window.history.replaceState({}, '', nextUrl)
    
          loadRequisiteTypeList(nextQuery)
        },
        [query, loadRequisiteTypeList]
    )

    const handleCreateRequisiteType = useCallback((newItem: IRequisiteType) => {            
        setRequisiteTypes((prev) => [...prev, newItem])
        setRequisiteTypeCount((prev) => prev + 1)            
    }, [])

    const handleUpdateRequisiteType = useCallback((updatedItem: IRequisiteType) => {
        setRequisiteTypes((prev) =>
            prev.map((item) => (item.id === updatedItem.id ? updatedItem : item))
        )
    }, [])

    const handleToggleSuccess = useCallback((id: number, enabled: boolean) => {
        setRequisiteTypes((prev) =>
            prev.map((item) =>
                item.id === id ? { ...item, enabled } : item
            )
        )
    }, [])

    const handleCreateClick = useCallback(() => {
        setEditingRequisiteType(null)
        setIsModalOpen(true)
    }, [])

    const handleEditRequisiteTypeClick = useCallback((item: IRequisiteType) => {
        setEditingRequisiteType(item)
        setIsModalOpen(true)
    }, [])

    useEffect(() => {
        if (initialized) return

        const init = async () => {
            await initializePage()
            applyFilters()
            setInitialized(true)
        }

        init()
    }, [initialized, applyFilters])

    useEffect(() => {
        if (!initialized) return

        if (location.hash === '#newRequisiteType') {
            setEditingRequisiteType(null)
            setIsModalOpen(true)
        } else {
            setIsModalOpen(false)
        }
    }, [location.hash, initialized])
    
    return (
        <div>
            <div className='card sticky-top-tickets mb-7'>
                <PageTitle breadcrumbs={breadCrumbs}>Типы реквизитов</PageTitle>
                <div className='card-header border-0 pt-5 pb-2'>
                    <h3 className='card-title align-items-start flex-column'>
                        <span className='card-label fw-bold fs-3 mb-1'>Типы реквизитов</span>
                        <span className="text-muted mt-1 fw-semibold fs-7">Всего: {isLoading || isInitializing ? <span className='spinner-border spinner-border-sm align-middle'></span> : requisiteTypeCount}</span>
                    </h3>
                    <div className="card-toolbar" style={{cursor: isLoading || isInitializing ? 'not-allowed' : 'pointer'}}>
                        <button 
                            className="btn btn-sm btn-primary"                            
                            disabled={isLoading || isInitializing}
                            onClick={handleCreateClick}
                        >
                            <KTIcon iconName="plus" className="fs-2" />
                            Добавить тип реквизита
                        </button>
                    </div>
                </div>
            </div>
            <div className="card">
                <div className="card-body py-3">
                    <Table>
                        <TableHead>
                            <tr>
                                <th className='min-w-40px'>ID</th>
                                <th className='min-w-150px'>Название</th>
                                <th className='min-w-100px'>Код</th>
                                <th className='min-w-100px'>Платежные методы</th>
                                <th className='min-w-150px'>Дата создания</th>
                                <th className='min-w-100px'></th>
                                <th className='min-w-100px'></th>
                            </tr>
                        </TableHead>
                        <TableBody>              
                            {!isLoading && !isInitializing && requisiteTypes.length > 0 ? (
                                requisiteTypes.map((requisiteType) => (
                                    <RequisiteTypesItem
                                        key={requisiteType.id}
                                        requisiteType={requisiteType}
                                        onEdit={handleEditRequisiteTypeClick}
                                        onToggleSuccess={handleToggleSuccess}
                                    />
                                ))
                            ) : (
                                <LoaderForTable isLoading={isLoading || isInitializing} colSpan={7} />
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>
            {pageCount > 1 && (
                <div className='d-flex justify-content-center mt-5'>
                    <ReactPaginate
                        breakLabel='...'
                        nextLabel='Следующая >'
                        onPageChange={(selected) =>
                            applyFilters({ page: selected.selected + 1 })
                        }
                        pageRangeDisplayed={5}
                        pageCount={pageCount}
                        previousLabel='< Предыдущая'
                        renderOnZeroPageCount={() => null}
                        className='pagination'
                        activeClassName='active'
                        disabledClassName='disabled'
                        nextClassName='page-item'
                        nextLinkClassName='page-link'
                        pageClassName='page-item'
                        pageLinkClassName='page-link'
                        previousClassName='page-item'
                        previousLinkClassName='page-link'
                        breakClassName='page-item'
                        breakLinkClassName='page-link'
                    />
                </div>
            )}
            {isModalOpen && 
                <ModalRequisiteType 
                    show={isModalOpen} 
                    onClose={(open) => {
                        setIsModalOpen(open)
                        if (!open) {
                            setEditingRequisiteType(null)
                        }
                    }}
                    onSave={
                        editingRequisiteType
                            ? handleUpdateRequisiteType
                            : handleCreateRequisiteType
                    }
                    initialRequisiteType={initialRequisiteType}
                    editingRequisiteType={editingRequisiteType}
                    optionsPaymentMethod={lookupPaymentMethods}
                />
            }
        </div>
    )
}
